import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import "./App.css";

const BACKEND_URL = "https://gda.ultrarunner.au/api";

const App = () => {
  const [winner, setWinner] = useState(null);
  const [history, setHistory] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newWinner, setNewWinner] = useState({ name: "", event: "", stravaLink: "", date: "" });

  useEffect(() => {
    Winner();
    fetchHistory();
    fetchLeaderboard();
    document.title = "The Golden Knob";
  }, []);

  // Random sayings for current winner
  const randomSayings = [
    "is vigorously jerking the Golden Knob like there's no tomorrow",
    "is giving the Golden Knob a workout no one asked for",
    "has the Golden Knob in their hands and they're not letting up",
    "is rubbing the Golden Knob like it's the last thing they’ll ever touch",
    "has got the Golden Knob in full motion and it's going nowhere",
    "is giving the Golden Knob the attention it craves, non-stop",
    "is stroking the Golden Knob with a level of commitment we haven't seen before",
    "is handling the Golden Knob like they're in the middle of an ultra-marathon",
    "is treating the Golden Knob like it’s their new favorite hobby",
    "is clutching the Golden Knob with the kind of care that suggests a deep connection"
  ];

  const Winner = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/current-winner`);
      const data = await response.json();
      setWinner(data);
    } catch (error) {
      console.error("Error fetching current winner:", error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/history`);
      const data = await response.json();
      setHistory(data);
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/leaderboard`);
      const data = await response.json();
      setLeaderboard(data);
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
    }
  };

// Retrieve the CF_Authorization token from the cookies
const getCFToken = () => {
  const cookie = document.cookie.split(';').find(cookie => cookie.trim().startsWith('CF_Authorization='));
  if (cookie) {
    return cookie.split('=')[1];  // Return the token part of the cookie
  }
  return null;  // Return null if the cookie is not found
};

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Get the token from localStorage (or wherever it's stored after login)
    //const token = localStorage.getItem("auth_token");  // Replace with your method of storing the token
    const authToken = getCFToken(); // Adjust according to your method for accessing the token
    console.log('Auth token:', authToken); // Log token to check if it's present

    //if (!token) {
    if (!authToken) {
      console.log("No auth token found, user is not logged in.");
      alert("You must be logged in to add a winner.");
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/current-winner`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          //"Authorization": `Bearer ${authToken}`  // Attach token to the Authorization header
          "CF-Access-JWT-Assertion": authToken
        },
        body: JSON.stringify({
          name: newWinner.name,
          event: newWinner.event,
          stravaLink: newWinner.stravaLink.trim() || null, // Send null if empty
          date: newWinner.date,
        }),
      });

      if (response.ok) {
        setNewWinner({ name: "", event: "", stravaLink: "", date: "" });
        window.location.href = "/";
      } else {
        const errorText = await response.text();
        alert(`Failed to add winner: ${errorText}`);
      }
    } catch (error) {
      alert("Error submitting winner.");
      console.error("Error submitting winner:", error);
    }
  };

  return (
    <Router>
      <div className="container">
        <h1 className="title">🏆 The Golden Knob 🏆</h1>
        <div className="description">
          The Golden Knob is bestowed upon the winner of the "race within the race". Any time two or more of the Sunco Runco touring party compete in the same distance at the same event, the Golden Knob will transfer to the first finisher of the longest distance.
        </div>
        {loading ? (
          <p>Loading knob data...</p>
        ) : (
          <Routes>
            <Route
              path="/"
              element={
                <div>
                  <div className="card" style={{ position: "relative" }}>
                    <h2>🥇 Current Holder</h2>
                    {winner ? (
                      <p>
                        <strong>{winner.name}</strong>{" "}
                        <span>{randomSayings[Math.floor(Math.random() * randomSayings.length)]}</span>{" "}
                        since {new Date(winner.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} at{' '}
                        {winner.stravaLink ? (
                          <a href={winner.stravaLink} target="_blank" rel="noopener noreferrer">
                            <strong>{winner.event}</strong>
                          </a>
                        ) : (
                          <strong>{winner.event}</strong>
                        )}
                        !
                      </p>
                    ) : (
                      <p>No winner recorded yet.</p>
                    )}

                    <a href="/admin" className="admin-link">
                      Add a new winner
                    </a>
                  </div>

                  <div className="card">
                    <h2>📜 Previous Knob Pullers</h2>
                    {history.length > 0 ? (
                      <ul>
                        {history.map((entry, index) => (
                          <li key={index}>
                            <strong>
                              {new Date(entry.date).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })}
                              :
                            </strong>{" "}
                            {entry.name} at{" "}
                            {entry.stravaLink ? (
                              <a
                                href={entry.stravaLink}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {entry.event}
                              </a>
                            ) : (
                              entry.event
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p>No past winners recorded yet.</p>
                    )}
                  </div>

                  <div className="card">
                    <h2>🏅 Leaderboard</h2>
                    {leaderboard.length > 0 ? (
                      <ul>
                        {leaderboard.map((entry, index) => (
                          <li key={index}>
                            <strong>{entry.name}</strong>: {entry.count} {entry.count === 1 ? "win" : "wins"}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p>No leaderboard data available.</p>
                    )}
                  </div>
                </div>
              }
            />

            <Route
              path="/admin"
              element={
                <div className="card" style={{ position: "relative" }}>
                  <h2>📝 Add New Winner</h2>
                  <form
                    onSubmit={handleSubmit}
                    className="admin-form"
                  >
                    <label>
                      Name of Winner:
                      <input
                        type="text"
                        placeholder="Name of Winner"
                        value={newWinner.name}
                        onChange={(e) => setNewWinner({ ...newWinner, name: e.target.value })}
                        required
                      />
                    </label>

                    <label>
                      Event Name:
                      <input
                        type="text"
                        placeholder="Event Name"
                        value={newWinner.event}
                        onChange={(e) => setNewWinner({ ...newWinner, event: e.target.value })}
                        required
                      />
                    </label>

                    <label>
                      Strava Link (Optional):
                      <input
                        type="text"
                        placeholder="Strava Link"
                        value={newWinner.stravaLink}
                        onChange={(e) => setNewWinner({ ...newWinner, stravaLink: e.target.value })}
                      />
                    </label>

                    <label>
                      Date of Win:
                      <input
                        type="date"
                        value={newWinner.date}
                        onChange={(e) => setNewWinner({ ...newWinner, date: e.target.value })}
                        required
                      />
                    </label>

                    <button type="submit">Add Winner</button>
                  </form>

                  <a href="/" className="return-home">
                    Return to home page
                  </a>
                </div>
              }
            />
          </Routes>
        )}
      </div>
    </Router>
  );
};

export default App;
